import React from 'react';
import OmInputFieldWrapper from '../forms/OmInputFieldWrapper';
import { createActionHandler, roundCurrency } from '../../util';
import ResultCard from './ResultCardSection';
import {
    setBuildWealthOnceOffValue,
    setMonthlyValue,
    calculateBuildWealth,
} from '@om/redux/actions/investmentCalculator/buildWealth';

import { useDispatch, useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import {
    getAmountOnceOffValue,
    getMonthlyAmountValue,
    getCalculateDisabled,
} from '@om/redux/selectors/investmentCalculator/buildWealthCalculator';
import OmDropdownField from '../forms/OmDropdownField';
import {
    getDropsReference,
    getGoalsReference,
    getSelectedDrop,
    getSelectedDuration,
    getShowBuildWealthDropField,
    getShowBuildWealthSplitForm,
    getShowRecommendation,
} from '@om/redux/selectors/investmentCalculator/investmentCalculator';
import {
    setSelectedDrop,
    setSelectedDuration,
} from '@om/redux/actions/investmentCalculator/investmentCalculator';
import { useBindCustomEvent } from '../../hooks/useBindCustomEvent';
import {
    getBalancedFundValue,
    getEquityFundValue,
    getIsEvenSplit,
    getSplitError,
    getTotalPercentage,
    getEquityFundSplitValue,
    getBalancedFundSplitValue,
} from '@om/redux/selectors/investmentCalculator/buildWealthSplit';
import {
    deselectEvenSplit,
    selectEvenSplit,
    setBalancedFundValue,
    setEquityFundValue,
} from '@om/redux/actions/investmentCalculator/buildWealthFundSplit';

import {
    OmInvestmentCalcSection,
    OmApplicationFundSplitList,
    OmApplicationFundSplitCard,
} from '@om/component-library-react';

import TwoColumnImageLeft from '../TwoColumnImageLeft';
import TwoColumnImageRight from '../TwoColumnImageRight';

const BuildWealthCalculator = ({
    slot,
    twoColumnImageLeftContent,
    twoColumnImageRightContent,
}) => {
    const dispatch = useDispatch();

    const {
        amountOnceOff,
        monthlyAmount,
        balancedFund,
        equityFund,
        calculateButtonDisabled,
        durationsReference,
        selectedDuration,
        dropsReference,
        selectedDrop,
        showSplitForm,
        showDropField,
        isEvenSplit,
        splitError,
        totalPercentage,
        equitySplitValue,
        balancedSplitValue,
        showRecommendation,
    } = useSelector(
        createStructuredSelector({
            amountOnceOff: getAmountOnceOffValue,
            monthlyAmount: getMonthlyAmountValue,
            calculateButtonDisabled: getCalculateDisabled,
            dropsReference: getDropsReference,
            durationsReference: getGoalsReference,
            selectedDrop: getSelectedDrop,
            selectedDuration: getSelectedDuration,
            showSplitForm: getShowBuildWealthSplitForm,
            showDropField: getShowBuildWealthDropField,
            balancedFund: getBalancedFundValue,
            equityFund: getEquityFundValue,
            isEvenSplit: getIsEvenSplit,
            splitError: getSplitError,
            totalPercentage: getTotalPercentage,
            equitySplitValue: getEquityFundSplitValue,
            balancedSplitValue: getBalancedFundSplitValue,
            showRecommendation: getShowRecommendation,
        }),
    );

    const ref = useBindCustomEvent('checkBoxChanged', () => {
        if (isEvenSplit) {
            dispatch(deselectEvenSplit());
            // dispatch(discardEvenSplit());
            return;
        }
        dispatch(selectEvenSplit());
    });

    return (
        <OmInvestmentCalcSection slot={slot}>
            <div slot='form-content'>
                <h4>Invest to Build Wealth</h4>
                <h6 className='no-bottom-margin'>
                    How much do you want to invest once-off?
                </h6>
                <OmInputFieldWrapper
                    isSensitive={true}
                    preset='KES'
                    prefixString='KES'
                    required={true}
                    size='full'
                    formId='build-wealth-calculator'
                    name='amountOnceOff'
                    mask='thousands-spaced'
                    keyboardMode='numeric'
                    hintText=''
                    reduxValue={amountOnceOff}
                    persistHandler={createActionHandler(
                        dispatch,
                        setBuildWealthOnceOffValue,
                    )}
                />
                <h6 className='no-bottom-margin'>
                    How much do you want to save monthly?
                </h6>
                <OmInputFieldWrapper
                    isSensitive={true}
                    preset='KES'
                    prefixString='KES'
                    required={true}
                    size='full'
                    formId='investment-calculator'
                    name='amountMonthly'
                    mask='thousands-spaced'
                    keyboardMode='numeric'
                    hintText=''
                    align='left'
                    reduxValue={monthlyAmount}
                    persistHandler={createActionHandler(
                        dispatch,
                        setMonthlyValue,
                    )}
                />

                <h6 className='no-bottom-margin'>
                    How long do you want to invest your money?
                </h6>
                <OmDropdownField
                    useNativeMobileDropdown={true}
                    className='inline-field drop-down'
                    required='true'
                    size='small'
                    form-id='build-wealth-calculator'
                    placeholder='1 Month'
                    name='formOption'
                    align='left'
                    onSelectHandler={event =>
                        dispatch(setSelectedDuration(event.detail.id))
                    }
                    reduxValue={selectedDuration}
                    options={durationsReference?.map(option => ({
                        value: option.duration,
                        label: option.description,
                    }))}
                />

                {showDropField ? (
                    <>
                        <h6 className='no-bottom-margin'>
                            How much can you afford to lose in a year?
                        </h6>
                        <OmDropdownField
                            useNativeMobileDropdown={true}
                            className='inline-field'
                            required='true'
                            size='small'
                            form-id='build-wealth-calculator'
                            name='formOption'
                            placeholder='0%'
                            align='left'
                            onSelectHandler={event =>
                                dispatch(setSelectedDrop(event.detail.id))
                            }
                            reduxValue={selectedDrop}
                            options={dropsReference?.map(option => ({
                                value: option.drop,
                                label: option.description,
                            }))}
                        />
                    </>
                ) : null}

                <h6 className='no-bottom-margin'>
                    Please note that this is non-contractual
                </h6>

                {showSplitForm ? (
                    <div className='split-form'>
                        <h6 className='no-bottom-margin'>
                            Split your <strong>investment between funds</strong>
                        </h6>
                        <OmApplicationFundSplitList
                            evenSplit={isEvenSplit}
                            totalPercentage={totalPercentage}
                            showAddMoreFundsButton={false}
                            should-display-add-funds-button={false}
                            totalValue={
                                'KES ' +
                                roundCurrency(
                                    balancedSplitValue + equitySplitValue,
                                )
                            }>
                            <div slot='fund-cards'>
                                <OmApplicationFundSplitCard
                                    fundName={'Equity Fund'}
                                    value={
                                        'KES ' + roundCurrency(equitySplitValue)
                                    }
                                    hideDeleteButton={false}>
                                    <OmInputFieldWrapper
                                        align='left'
                                        type={'number'}
                                        maxLength={3}
                                        slot='percentage-input'
                                        required={true}
                                        size='small'
                                        formId='build-wealth-calculator'
                                        name='equityFund'
                                        keyboardMode='numeric'
                                        hintText=''
                                        reduxValue={equityFund}
                                        persistHandler={createActionHandler(
                                            dispatch,
                                            setEquityFundValue,
                                        )}
                                    />
                                </OmApplicationFundSplitCard>
                                <OmApplicationFundSplitCard
                                    fundNname={'Balanced Fund'}
                                    errorMessage={splitError}
                                    value={
                                        'KES ' +
                                        roundCurrency(balancedSplitValue)
                                    }
                                    hideDeleteButton={false}
                                    fundOne={false}>
                                    <OmInputFieldWrapper
                                        align='left'
                                        type={'number'}
                                        maxLength={3}
                                        slot='percentage-input'
                                        required={true}
                                        size='small'
                                        formId='build-wealth-calculator'
                                        name='balancedFund'
                                        keyboardMode='numeric'
                                        hintText=''
                                        reduxValue={balancedFund}
                                        persistHandler={createActionHandler(
                                            dispatch,
                                            setBalancedFundValue,
                                        )}
                                    />
                                </OmApplicationFundSplitCard>
                            </div>
                        </OmApplicationFundSplitList>
                    </div>
                ) : null}
            </div>
            <ResultCard
                calculateButtonDisabled={calculateButtonDisabled}
                setInvestmentResult={() => dispatch(calculateBuildWealth())}
            />

            <div slot='product-offering'>
                {showRecommendation && (
                    <>
                        <TwoColumnImageLeft
                            twoColumnImageLeftContent={
                                twoColumnImageLeftContent
                            }
                        />
                        <TwoColumnImageRight
                            twoColumnImageRightContent={
                                twoColumnImageRightContent
                            }
                        />
                    </>
                )}
            </div>
        </OmInvestmentCalcSection>
    );
};

export default BuildWealthCalculator;
